import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';

const DashboardContext = createContext();

export function useDashboard() {
  return useContext(DashboardContext);
}

export function DashboardProvider({ children }) {
  // Table Management
  const [tables, setTables] = useState([]);
  const [currentTableId, setCurrentTableId] = useState(null);
  const [tableData, setTableData] = useState({});
  // What produced each table's rows: the filters the successful query ran with
  // and what it reported (`{ filters, rangeFilters, truncated, rowCount,
  // shownRows }`). Written in the same moment as the rows and kept for as long
  // as they are, so the grid's truncation notice and both export paths always
  // describe the rows on screen — never the filters of a query that failed,
  // and never nothing because the Data page remounted.
  const [fetchedByTable, setFetchedByTable] = useState({});

  // Date Range
  const [startDateByTable, setStartDateByTable] = useState({});
  const [endDateByTable, setEndDateByTable] = useState({});

  // Filter State
  const [filterStateByTable, setFilterStateByTable] = useState({});
  
  // UI State
  const [showTooltips, setShowTooltips] = useState(true);

  const { companyName } = useAuth();

  const filterState = currentTableId ? (filterStateByTable[currentTableId] || {}) : {};
  const startDate = currentTableId ? (startDateByTable[currentTableId] || '') : '';
  const endDate = currentTableId ? (endDateByTable[currentTableId] || '') : '';

  const setFilterState = useCallback((nextState) => {
    if (!currentTableId) return;
    setFilterStateByTable(prev => {
      const currentState = prev[currentTableId] || {};
      const resolvedState = typeof nextState === 'function' ? nextState(currentState) : nextState;
      if (resolvedState === currentState) return prev;
      return { ...prev, [currentTableId]: resolvedState };
    });
  }, [currentTableId]);

  const setStartDate = useCallback((nextDate) => {
    if (!currentTableId) return;
    setStartDateByTable(prev => {
      const currentDate = prev[currentTableId] || '';
      const resolvedDate = typeof nextDate === 'function' ? nextDate(currentDate) : nextDate;
      if (resolvedDate === currentDate) return prev;
      return { ...prev, [currentTableId]: resolvedDate };
    });
  }, [currentTableId]);

  const setEndDate = useCallback((nextDate) => {
    if (!currentTableId) return;
    setEndDateByTable(prev => {
      const currentDate = prev[currentTableId] || '';
      const resolvedDate = typeof nextDate === 'function' ? nextDate(currentDate) : nextDate;
      if (resolvedDate === currentDate) return prev;
      return { ...prev, [currentTableId]: resolvedDate };
    });
  }, [currentTableId]);

  // Clear table-scoped state when company changes
  useEffect(() => {
    if (!companyName) {
      setTableData({});
      setFetchedByTable({});
      setCurrentTableId(null);
      setFilterStateByTable({});
      setStartDateByTable({});
      setEndDateByTable({});
    }
  }, [companyName]);

  // Everything this context keys by table id: the last query's rows, the filter
  // state, and the date range. A table id is its sanitised name, so a deleted
  // table recreated under the same name would otherwise inherit all three.
  const clearTableState = useCallback((tableId) => {
    const drop = (prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, tableId)) return prev;
      const next = { ...prev };
      delete next[tableId];
      return next;
    };
    setTableData(drop);
    setFetchedByTable(drop);
    setFilterStateByTable(drop);
    setStartDateByTable(drop);
    setEndDateByTable(drop);
  }, []);

  const clearAllTableData = () => {
    setTableData({});
  };

  const updateFilterState = useCallback((newFilters) => {
    setFilterState(prev => ({
      ...prev,
      ...newFilters
    }));
  }, [setFilterState]);

  const value = {
    // Table Management
    tables,
    setTables,
    currentTableId,
    setCurrentTableId,
    tableData,
    setTableData,
    fetchedByTable,
    setFetchedByTable,
    clearTableState,
    clearAllTableData,

    // Date Range
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Filter State
    filterState,
    setFilterState,
    updateFilterState,

    // UI State
    showTooltips,
    setShowTooltips,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}
